import React, { useState } from "react";
import { motion } from "@/lib/motion";
import { Cake, Heart, Baby, Briefcase, Users, PartyPopper, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { Reveal } from "@/components/Reveal";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const EVENTS = [
    { icon: Cake, label: "Birthdays" }, { icon: Heart, label: "Weddings" }, { icon: Baby, label: "Baby Showers" },
    { icon: Briefcase, label: "Corporate" }, { icon: Users, label: "Family Gatherings" }, { icon: PartyPopper, label: "Holidays" },
];

export default function CateringPage() {
    const [form, setForm] = useState({ name: "", email: "", phone: "", event_type: "Birthday", event_date: "", guest_count: "", message: "" });
    const [sent, setSent] = useState(false);
    const [loading, setLoading] = useState(false);
    const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await api.post("/catering", { ...form, guest_count: form.guest_count ? Number(form.guest_count) : null });
            setSent(true);
            toast.success("Request received! We'll be in touch within 24 hours.");
        } catch (e2) {
            toast.error(apiError(e2));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div>
            <section className="relative overflow-hidden bg-caramel-dark py-24 text-cream-50">
                <div className="absolute inset-0 opacity-25" style={{ backgroundImage: "url(/img/peach.jpg)", backgroundSize: "cover", backgroundPosition: "center" }} />
                <div className="relative mx-auto max-w-3xl px-5 text-center">
                    <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-sm font-semibold uppercase tracking-widest text-banana">Catering & Events</motion.p>
                    <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="mt-3 font-serif text-4xl font-semibold sm:text-5xl">Pudding trays for every celebration</motion.h1>
                    <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }} className="mt-4 text-cream-100/80">From intimate gatherings to grand events, we'll make dessert the moment everyone remembers.</motion.p>
                </div>
            </section>

            <section className="mx-auto max-w-7xl px-5 py-16 lg:px-8">
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
                    {EVENTS.map((e, i) => (
                        <Reveal key={e.label} delay={i * 0.05}>
                            <div className="rounded-2xl border border-caramel/10 bg-white p-5 text-center shadow-warm">
                                <e.icon size={28} className="mx-auto text-caramel" />
                                <p className="mt-2 text-sm font-medium text-roast">{e.label}</p>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </section>

            <section className="mx-auto max-w-3xl px-5 pb-24 lg:px-8">
                <Reveal>
                    <div className="rounded-3xl border border-caramel/10 bg-white p-8 shadow-warm sm:p-10">
                        {sent ? (
                            <div className="py-8 text-center" data-testid="catering-success">
                                <CheckCircle2 size={56} className="mx-auto text-caramel" />
                                <h2 className="mt-4 font-serif text-3xl text-roast">Thank you!</h2>
                                <p className="mt-2 text-mocha">Your catering request is in. Our events team will reach out within 24 hours with a custom quote.</p>
                            </div>
                        ) : (
                            <>
                                <h2 className="font-serif text-3xl text-roast">Request a Catering Quote</h2>
                                <p className="mt-1 text-mocha">Tell us about your event and we'll craft the perfect pudding spread.</p>
                                <form onSubmit={submit} className="mt-6 space-y-4" data-testid="catering-form">
                                    <div className="grid gap-4 sm:grid-cols-2">
                                        <Input required data-testid="catering-name" value={form.name} onChange={set("name")} placeholder="Your name" className="h-12 rounded-xl border-caramel/15 focus-visible:ring-caramel" />
                                        <Input required type="email" data-testid="catering-email" value={form.email} onChange={set("email")} placeholder="Email" className="h-12 rounded-xl border-caramel/15 focus-visible:ring-caramel" />
                                    </div>
                                    <div className="grid gap-4 sm:grid-cols-2">
                                        <Input data-testid="catering-phone" value={form.phone} onChange={set("phone")} placeholder="Phone (optional)" className="h-12 rounded-xl border-caramel/15 focus-visible:ring-caramel" />
                                        <Select value={form.event_type} onValueChange={set("event_type")}>
                                            <SelectTrigger data-testid="catering-event-type" className="h-12 rounded-xl border-caramel/15"><SelectValue /></SelectTrigger>
                                            <SelectContent>{EVENTS.map((e) => <SelectItem key={e.label} value={e.label}>{e.label}</SelectItem>)}</SelectContent>
                                        </Select>
                                    </div>
                                    <div className="grid gap-4 sm:grid-cols-2">
                                        <Input type="date" data-testid="catering-date" value={form.event_date} onChange={set("event_date")} className="h-12 rounded-xl border-caramel/15 focus-visible:ring-caramel" />
                                        <Input type="number" min="1" data-testid="catering-guests" value={form.guest_count} onChange={set("guest_count")} placeholder="Guest count" className="h-12 rounded-xl border-caramel/15 focus-visible:ring-caramel" />
                                    </div>
                                    <Textarea data-testid="catering-message" value={form.message} onChange={set("message")} placeholder="Tell us more about your event…" className="rounded-xl border-caramel/15 focus-visible:ring-caramel" />
                                    <Button type="submit" disabled={loading} data-testid="catering-submit" className="h-12 w-full rounded-full bg-caramel text-base text-cream-50 hover:bg-caramel-dark">{loading ? "Sending…" : "Request Quote"}</Button>
                                </form>
                            </>
                        )}
                    </div>
                </Reveal>
            </section>
        </div>
    );
}
